import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, GalleryHorizontal, Image as ImageIcon, Pencil, Plus, Trash2, X } from "lucide-react";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { getRuntimeConfigEntry, setRuntimeConfig } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  emptyEventsConfig, newScheduleRow, normalizeEventsConfig, validateEventsConfig,
  type EventsConfig, type LiveOpsListKey, type LiveOpsScheduleRow,
} from "@/lib/event-schema";
import "./events.css";

const EVENTS_KEY = "EC2_EVENTS";

type EditorState =
  | { mode: "new"; listKey: LiveOpsListKey }
  | { mode: "edit"; listKey: LiveOpsListKey; row: LiveOpsScheduleRow; index: number };

const LISTS: [LiveOpsListKey, string, string, typeof ImageIcon][] = [
  ["lobbyBanners", "Lobby banners", "lobby banner", ImageIcon],
  ["eventBanners", "Event banners", "event banner", GalleryHorizontal],
  ["events", "Events", "event", CalendarDays],
];
const listLabel = (key: LiveOpsListKey) => LISTS.find(l => l[0] === key)![1];
const errorText = (error: unknown) => error instanceof Error ? error.message : String(error);

function parseEventsConfig(value: string | undefined): EventsConfig {
  if (!value?.trim()) return emptyEventsConfig();
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("Stored event configuration must be a JSON object.");
  return normalizeEventsConfig(parsed);
}
const dateFormat = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormat.format(date);
}
const customJsonToText = (value: LiveOpsScheduleRow["customJSON"]) => value === null ? "" : JSON.stringify(value, null, 2);

/** Modal ws-dialog that opens on mount; Escape is ignored while busy. */
function Modal({ label, busy, wide, onClose, children }: { label: string; busy?: boolean; wide?: boolean; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => { const dialog = ref.current; if (dialog && !dialog.open) dialog.showModal(); return () => dialog?.close(); }, []);
  return <dialog ref={ref} className={cn("ws-dialog", wide && "ev-dialog-wide")} aria-label={label}
    onCancel={e => { e.preventDefault(); if (!busy) onClose(); }}>{children}</dialog>;
}

export function EventsWorkspace() {
  const readOnly = usePreviewReadOnly();
  const queryClient = useQueryClient();
  const [list, setList] = useState<LiveOpsListKey>("lobbyBanners");
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ listKey: LiveOpsListKey; index: number; id: string } | null>(null);
  const query = useQuery({
    queryKey: ["runtime-config-entry", EVENTS_KEY],
    queryFn: async () => parseEventsConfig((await getRuntimeConfigEntry(EVENTS_KEY))?.value),
  });
  const config = query.data ?? emptyEventsConfig();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["runtime-config-entry", EVENTS_KEY] });
  const writeConfig = useMutation({
    mutationFn: (next: EventsConfig) => setRuntimeConfig(EVENTS_KEY, JSON.stringify(next)),
    onSuccess: invalidate,
  });

  function toggleActive(listKey: LiveOpsListKey, index: number) {
    const rows = [...config[listKey]];
    const row = rows[index];
    if (!row) return;
    rows[index] = { ...row, active: !row.active };
    writeConfig.mutate({ ...config, [listKey]: rows });
  }
  function deleteRow(listKey: LiveOpsListKey, index: number) {
    writeConfig.mutate({ ...config, [listKey]: config[listKey].filter((_, i) => i !== index) }, { onSuccess: () => setConfirmDelete(null) });
  }
  function openDelete(index: number, id: string) { writeConfig.reset(); setConfirmDelete({ listKey: list, index, id }); }
  function openEditor(next: EditorState) { writeConfig.reset(); setEditor(next); }

  const rows = config[list];
  const writing = writeConfig.isPending;

  return <WorkspaceShell crumbs={[{ label: "Game configuration" }, { label: "Events" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page">
      <div className="ws-head">
        <h1>Events</h1>
        <div className="ws-head-actions">
          <button className="pw-button pw-button-primary" onClick={() => openEditor({ mode: "new", listKey: list })} disabled={editor !== null || readOnly || query.isPending || query.isError}>
            <Plus size={14} />New {LISTS.find(l => l[0] === list)![2]}</button>
        </div>
      </div>
      {query.isError && <div className="ws-alert" role="alert">Failed to load: {errorText(query.error)}</div>}
      {writeConfig.isError && !editor && !confirmDelete && <div className="ws-alert" role="alert">{errorText(writeConfig.error)}</div>}
      <div className="pw-heroes ev-layout">
        <div className="pw-hero-picker" role="group" aria-label="Schedule list">{LISTS.map(([key, label, , Icon]) =>
          <button key={key} onClick={() => setList(key)} aria-pressed={list === key} className={cn("pw-hero-option", list === key && "is-active")}>
            <span className="pw-category-icon"><Icon size={18} strokeWidth={1.6} /></span>
            <span><strong>{label}</strong><small>{query.data ? `${config[key].length} row${config[key].length === 1 ? "" : "s"}` : "—"}</small></span>
          </button>)}
        </div>
        <div className="pw-hero-content">
          <div className="ws-card is-flush">
            {query.isPending ? <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading…</div>
              : query.isError ? <div className="ws-empty">Unavailable</div>
              : rows.length === 0 ? <div className="ws-empty">No {listLabel(list).toLowerCase()}</div>
              : <div className="ws-table ev-table" role="table" aria-label={listLabel(list)}>
                <div className="ws-table-row ws-table-head ev-row" role="row">{["ID", "contentId", "Status", "Schedule", ""].map((h, i) => <span key={i} role="columnheader">{h}</span>)}</div>
                {rows.map((row, index) => <div key={`${row.id}-${index}`} className="ws-table-row ev-row" role="row">
                  <span role="cell" className="ev-id ws-mono" title={row.id}>{row.id}</span>
                  <span role="cell" className="ev-content ws-mono" title={row.contentId}>{row.contentId}</span>
                  <span role="cell" className="ev-status">
                    <button type="button" className={cn("ev-toggle", row.active && "is-on")} onClick={() => toggleActive(list, index)} disabled={writing || readOnly}
                      aria-pressed={row.active} title="Toggle active">{row.active ? "Active" : "Inactive"}</button>
                  </span>
                  <span role="cell" className="ev-dates">{formatDate(row.startDate)}<span aria-hidden="true">→</span>{formatDate(row.endDate)}</span>
                  <span role="cell" className="ev-actions">
                    <button className="pw-icon-button" aria-label={`Edit ${row.id}`} title="Edit" disabled={editor !== null || readOnly} onClick={() => openEditor({ mode: "edit", listKey: list, row, index })}><Pencil size={14} /></button>
                    <button className="pw-icon-button ev-delete" aria-label={`Delete ${row.id}`} title="Delete" disabled={readOnly} onClick={() => openDelete(index, row.id)}><Trash2 size={14} /></button>
                  </span>
                </div>)}
              </div>}
          </div>
        </div>
      </div>
    </div></div>

    {editor && <RowEditor key={`${editor.listKey}-${editor.mode === "edit" ? editor.row.id : "new"}`} state={editor} config={config} readOnly={readOnly}
      onClose={() => setEditor(null)} onSaved={next => writeConfig.mutate(next, { onSuccess: () => setEditor(null) })} saving={writing} saveError={writeConfig.error} />}

    {confirmDelete && <Modal label="Delete row" busy={writing} onClose={() => setConfirmDelete(null)}>
      <div className="ws-dialog-head"><h2>Delete <span className="ws-mono">{confirmDelete.id}</span>?</h2></div>
      <div className="ws-dialog-body">
        <span className="ws-muted">From {listLabel(confirmDelete.listKey)} · can't be undone</span>
        {writeConfig.isError && <div className="ws-alert" role="alert">{errorText(writeConfig.error)}</div>}
      </div>
      <div className="ws-dialog-foot">
        <button className="pw-button" onClick={() => setConfirmDelete(null)} disabled={writing}>Cancel</button>
        <button className="pw-button pw-button-danger" onClick={() => deleteRow(confirmDelete.listKey, confirmDelete.index)} disabled={writing || readOnly}>{writing ? "Deleting…" : "Delete"}</button>
      </div>
    </Modal>}
  </WorkspaceShell>;
}

function RowEditor({ state, config, readOnly, onClose, onSaved, saving, saveError }: {
  state: EditorState; config: EventsConfig; readOnly: boolean; onClose: () => void; onSaved: (next: EventsConfig) => void; saving: boolean; saveError: unknown;
}) {
  const isEdit = state.mode === "edit";
  const [row, setRow] = useState<LiveOpsScheduleRow>(isEdit ? state.row : newScheduleRow());
  const [customJsonText, setCustomJsonText] = useState(customJsonToText(isEdit ? state.row.customJSON : null));
  const [customJsonError, setCustomJsonError] = useState<string | null>(null);
  const basePath = `${state.listKey}${isEdit ? `[${state.index}]` : `[${config[state.listKey].length}]`}`;

  const candidate = useMemo(() => {
    const list = [...config[state.listKey]];
    const nextRow: LiveOpsScheduleRow = { ...row, id: row.id.trim(), contentId: row.contentId.trim() };
    if (isEdit) list[state.index] = nextRow;
    else list.push(nextRow);
    return { ...config, [state.listKey]: list };
  }, [config, state, row, isEdit]);

  const issues = useMemo(() => {
    const fromSchema = validateEventsConfig(candidate);
    return customJsonError ? [...fromSchema, { severity: "error" as const, path: `${basePath}.customJSON`, message: customJsonError }] : fromSchema;
  }, [candidate, customJsonError, basePath]);
  const errors = issues.filter(issue => issue.severity === "error");
  const issueText = (path: string) => issues.filter(issue => issue.path === path).map(issue => issue.message).join(" ");
  // Issues outside this row's own fields still block saving, so list them separately.
  const fieldPaths = ["id", "contentId", "startDate", "endDate", "customJSON"].map(f => `${basePath}.${f}`);
  const otherIssues = issues.filter(issue => !fieldPaths.includes(issue.path));

  function applyCustomJson(text: string) {
    setCustomJsonText(text);
    const trimmed = text.trim();
    if (!trimmed) { setCustomJsonError(null); setRow(current => ({ ...current, customJSON: null })); return; }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) { setCustomJsonError("customJSON must be a JSON object or empty."); return; }
      setCustomJsonError(null);
      setRow(current => ({ ...current, customJSON: parsed as Record<string, unknown> }));
    } catch { setCustomJsonError("customJSON is not valid JSON."); }
  }
  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (errors.length === 0 && !readOnly) onSaved(candidate);
  }
  const Issue = ({ path }: { path: string }) => { const text = issueText(path); return text ? <span className="ev-issue">{text}</span> : null; };
  const title = isEdit ? `Edit ${state.row.id}` : `New ${LISTS.find(l => l[0] === state.listKey)![2]}`;

  return <Modal label={title} busy={saving} wide onClose={onClose}>
    <form className="ws-form ev-editor" onSubmit={submit}>
      <div className="ws-dialog-head"><h2>{title}</h2><button type="button" className="pw-icon-button" aria-label="Close" onClick={onClose} disabled={saving}><X size={16} /></button></div>
      <div className="ws-dialog-body">
        <div className="ev-grid">
          <div className="ev-cell"><input aria-label="ID" placeholder="ID" value={row.id} disabled={isEdit} onChange={e => setRow({ ...row, id: e.target.value })} /><Issue path={`${basePath}.id`} /></div>
          <div className="ev-cell"><input aria-label="contentId" placeholder="contentId" title="Addressable Banner/Event ScriptableObject id" value={row.contentId} onChange={e => setRow({ ...row, contentId: e.target.value })} /><Issue path={`${basePath}.contentId`} /></div>
          <div className="ev-cell ev-date"><DateTimePicker value={row.startDate || null} placeholder="Start" onChange={value => setRow({ ...row, startDate: value ?? "" })} /><Issue path={`${basePath}.startDate`} /></div>
          <div className="ev-cell ev-date"><DateTimePicker value={row.endDate || null} placeholder="End" onChange={value => setRow({ ...row, endDate: value ?? "" })} /><Issue path={`${basePath}.endDate`} /></div>
        </div>
        <label className="ev-check"><input type="checkbox" checked={row.active} onChange={e => setRow({ ...row, active: e.target.checked })} />Active</label>
        <div className="ev-cell"><textarea className="ev-json" aria-label="customJSON (optional)" placeholder="customJSON (optional)" spellCheck={false} value={customJsonText} onChange={e => applyCustomJson(e.target.value)} />
          <Issue path={`${basePath}.customJSON`} /></div>
        {otherIssues.length > 0 && <div className={cn("ws-alert", !otherIssues.some(i => i.severity === "error") && "is-warn")} role="alert">
          <ul className="ev-issues">{otherIssues.map((issue, index) => <li key={`${issue.path}-${index}`}><span className="ws-mono">{issue.path || "root"}</span> {issue.message}</li>)}</ul>
        </div>}
        {Boolean(saveError) && <div className="ws-alert" role="alert">{errorText(saveError)}</div>}
      </div>
      <div className="ws-dialog-foot">
        <button type="button" className="pw-button" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="submit" className="pw-button pw-button-primary" disabled={saving || errors.length > 0 || readOnly}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </form>
  </Modal>;
}
